import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import Lottie from 'react-lottie';

import { Box, Stack, xcss } from '@atlaskit/primitives';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useCloudStatusRedirection } from '../../hooks/use-cloud-status-redirection';
import ProgressBar from '../../sections/progress-bar';
import ErrorView from '../error/error-view';

import { LoomOnboardingText } from './components/loom-onboarding-text';
import LoomProvisioningAnimation from './lotties/loom-provisioning-animation.json';
import { useOnboardingData } from './utilities/save-data';

type Screen = 'provisioning' | 'timeout' | 'error';

/**
 * Passive view rendered by `LoomOnboardingView` once the user has finished
 * answering onboarding questions. Its sole side effect is telling the
 * `CloudStatusRedirectionProvider` that the onboarding payload is ready —
 * the provider owns the actual Loom SSO redirect and fires it as soon as
 * `createCloudStatus === 'success'`.
 */
export const AfterOnboardingStep = () => {
	const { createCloudStatus } = useCloudStatus();
	const { clearOnboardingData } = useOnboardingData();
	const { setLoomOnboardingReady } = useCloudStatusRedirection();
	const [screen, setScreen] = useState<Screen>('provisioning');

	useEffect(() => {
		// Reaching this component means onboarding is complete. Hand the final
		// snapshot to the provider and clear localStorage so a subsequent signup
		// on the same browser starts fresh.
		const onboardingData = clearOnboardingData();
		setLoomOnboardingReady?.(onboardingData);
	}, [clearOnboardingData, setLoomOnboardingReady]);

	useEffect(() => {
		switch (createCloudStatus) {
			case 'timeout':
			case 'timeout-recoverable':
				setScreen('timeout');
				return;
			case 'error':
			case 'error-unrecoverable':
				setScreen('error');
				return;
			case 'success':
			case 'pending':
			default:
				setScreen('provisioning');
				return;
		}
	}, [createCloudStatus]);

	if (screen === 'error') {
		return <ErrorView bundle="loom" />;
	}

	const isTimeout = screen === 'timeout';

	return (
		<Box
			testId={isTimeout ? 'after-onboarding-step-timeout' : 'after-onboarding-step-provisioning'}
			xcss={centeredBoxStyles}
		>
			<div css={[contentRowStyles, contentWidthStyles]}>
				<Stack space="space.150" alignInline="center">
					<div css={lottieContainerStyles} aria-hidden="true">
						<Lottie
							options={{
								loop: true,
								autoplay: true,
								animationData: LoomProvisioningAnimation,
							}}
						/>
					</div>
					<div css={progressBarWrapperStyles}>
						<ProgressBar suppressTimeoutState />
					</div>
					<LoomOnboardingText align="center">
						{isTimeout
							? 'This is taking a bit longer than expected. Keep this tab open so we can get you started right away.'
							: "Getting your workspace ready. Keep this tab open, this shouldn't take more than a minute."}
					</LoomOnboardingText>
				</Stack>
			</div>
		</Box>
	);
};

// Fill the remaining viewport and vertically center the content, while guaranteeing
// a minimum 144px gap between the top of the screen and the Lottie.
const centeredBoxStyles = xcss({
	display: 'grid',
	gridTemplateRows: 'minmax(144px, 1fr) auto 1fr',
	justifyItems: 'center',
	width: '100%',
	minHeight: 'calc(100vh - 8rem)',
});

// Place the content in the middle grid track so the top `minmax(144px, 1fr)` and
// bottom `1fr` tracks flank it.
const contentRowStyles = css({
	gridRow: 2,
});

// Constrain the whole content column to a single width so the Lottie
const contentWidthStyles = css({
	width: '100%',
	maxWidth: '600px',
});

const lottieContainerStyles = css({
	width: '100%',
});

const progressBarWrapperStyles = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
});
